import { Button, Switch, Text, VSpacer } from '@/components/DesignSystem';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { QueryKeys } from '@/constants/QueryKeys';
import {
  RewardsProgramModal,
} from '@/pages/Admin/HierarchyOfRetailers/Retailer/RewardsPrograms/RewardsProgramModal';
import { useSnackbar } from '@/providers/GlobalSnackbarProvider';
import { RetailerApi } from '@/utilities/api/RetailerApi';
import { RewardsProgramApi } from '@/utilities/api/RewardsProgramApi';
import { RewardsProgramEndpoint } from '@api/endpoints/RewardsProgramEndpoint';
import { ApiRetailer } from '@api/interfaces';
import { ApiRewardsProgram } from '@api/interfaces/ApiRewardsProgram';
import Add from '@mui/icons-material/Add';
import { Stack } from '@mui/material';
import { ProgramStatus } from '@shared/enums/ProgramStatus';
import { RewardsProgramType } from '@shared/enums/RewardsProgramType';
import {
  formatMediumDate,
  getJsDateFromString,
  getProgramStatus,
  getTierOverrideDates,
  omit,
} from '@shared/utilities';
import i18next from 'i18next';
import { DateTime } from 'luxon';
import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { RewardsProgramsTable } from './RewardsProgramsTable';

interface RewardsProgramsCardProps {
  disabled: boolean,
  isJobProcessing: boolean,
  programs?: ApiRewardsProgram[],
  retailer: ApiRetailer,
}

export const RewardsProgramsCard = ({
  disabled,
  isJobProcessing,
  programs,
  retailer,
}: RewardsProgramsCardProps) => {
  const { openSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [selectedProgramId, setSelectedProgramId] = useState<string | null>(null);
  const [showRewardsProgramModal, setShowRewardsProgramModal] = useState(false);
  const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState(false);
  const [showNoTierOverrideDataModal, setShowNoTierOverrideDataModal] = useState(false);
  const [tierOverrideTriggered, setTierOverrideTriggered] = useState(false);
  const [showGenerateTierOverrideButton, setShowGenerateTierOverrideButton] = useState(false);

  const agronomyPrograms = programs?.filter(
    (program) => program.type === RewardsProgramType.Agronomy,
  );

  useEffect(() => {
    const checkButtonVisibility = async () => {
      const hasExpiredProgram = agronomyPrograms?.filter(
        (program) => getProgramStatus(program) === ProgramStatus.Expired,
      );
      if (hasExpiredProgram?.length) {
        setShowGenerateTierOverrideButton(false);
        return;
      }

      const activeOrFutureProgram = getActiveOrFutureProgram();

      if (activeOrFutureProgram) {
        const hasPointsBeenRun =
          await RewardsProgramApi.hasPointsEngineBeenRun(
            activeOrFutureProgram.id,
          );
        setShowGenerateTierOverrideButton(!hasPointsBeenRun);
        return;
      }

      setShowGenerateTierOverrideButton(false);
    };

    void checkButtonVisibility();
  }, [agronomyPrograms]);

  const canAddProgram = useMemo(() => (
    !agronomyPrograms
    || agronomyPrograms.filter((program) => (
      getProgramStatus(program) === ProgramStatus.Future
    )).length < 1
  ), [agronomyPrograms]);

  const selectedProgram = useMemo(() => (
    agronomyPrograms?.find((program) => program.id === selectedProgramId)
  ), [agronomyPrograms, selectedProgramId]);

  const getActiveOrFutureProgram = () => {
    return (agronomyPrograms ?? []).sort(
      (a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime(),
    ).find(
      (program) => getProgramStatus(program) === ProgramStatus.Active
        || getProgramStatus(program) === ProgramStatus.Future,
    );
  };

  const forcedStartDate = useMemo(() => {
    const activeProgram = agronomyPrograms?.find((program) => (
      getProgramStatus(program) === ProgramStatus.Active
    ));
    const endDate = activeProgram?.endDate;
    return endDate
      ? DateTime
        .fromJSDate(getJsDateFromString(endDate))
        .plus({ days: 1 })
        .toFormat('yyyy-MM-dd')
      : undefined;
  }, [agronomyPrograms]);

  const { mutate: createProgram } = useMutation(
    (body: RewardsProgramEndpoint.Create.Request) => (
      RewardsProgramApi.create(body)
    ),
    {
      onSuccess: async () => {
        openSnackbar('Rewards program added');
        await queryClient.invalidateQueries(QueryKeys.GET_RETAILER_BY_ID);
        await queryClient.invalidateQueries(QueryKeys.GET_RETAILER_CATEGORY_MAPPINGS);
      },
      onError: (error: { message: string }) => {
        openSnackbar(error.message || 'An error occurred while adding a rewards program');
      },
    },
  );

  const { mutate: updateProgram } = useMutation(
    (body: RewardsProgramEndpoint.Update.Request & { id: string }) => (
      RewardsProgramApi.update(body.id, omit(body, ['id']))
    ),
    {
      onSuccess: async () => {
        openSnackbar('Rewards program updated');
        await queryClient.invalidateQueries(QueryKeys.GET_RETAILER_BY_ID);
        await queryClient.invalidateQueries(QueryKeys.GET_RETAILER_CATEGORY_MAPPINGS);
      },
      onError: (error: { message: string }) => {
        openSnackbar(error.message || 'An error occurred while updating the rewards program');
      },
    },
  );

  const { mutate: updateIsProgramCreationDisabled } = useMutation(
    (isProgramCreationDisabled: boolean) => (
      RetailerApi.update(retailer.id, { isProgramCreationDisabled })
    ),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(QueryKeys.GET_RETAILER_BY_ID);
      },
      onError: (error: { message: string }) => {
        openSnackbar(error.message || 'An error occurred while updating the retailer');
      },
    },
  );

  const { mutate: deleteProgram, isLoading: isDeletingProgram } = useMutation(
    async (id: string) => (
      RewardsProgramApi.delete(id)
    ),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(QueryKeys.GET_RETAILER_BY_ID);
        await queryClient.invalidateQueries(QueryKeys.GET_RETAILER_CATEGORY_MAPPINGS);
      },
      onError: (error: { message: string }) => {
        openSnackbar(error.message || 'An error occurred while updating the retailer');
      },
    },
  );

  const { mutate: initializeFarmers, isLoading: isInitializing } = useMutation(
    async () => (
      RetailerApi.initializeFarmerRewards(retailer.id)
    ),
    {
      onSuccess: async () => {
        openSnackbar('Farmers initialized for active program');
      },
      onError: (error: { message: string }) => {
        openSnackbar(error.message || 'An error occurred while updating the retailer');
      },
    },
  );

  const { data: programIdsWithPointsEngineRun } = useQuery(
    [QueryKeys.LIST_REWARDS_PROGRAMS, { retailerId: retailer.id, hasPointsEngineBeenRun: true }],
    async () => {
      return (await RewardsProgramApi.list({
        retailerId: retailer.id,
        hasPointsEngineBeenRun: true,
      })).map(({ id }) => id);
    },
    {
      onError: (error: { message: string }) => {
        openSnackbar(error.message || 'An error occurred while fetching program statuses');
      },
    },
  );

  return (
    <>
      <Stack>
        <Stack alignItems="center" direction="row" justifyContent="space-between">
          <Text category="title-small">
            REWARDS PROGRAMS
          </Text>
          <Stack alignItems="center" direction="row" justifyContent="space-between" spacing={3}>
            <>
              {!agronomyPrograms?.length && (
                <Switch
                  checked={retailer.isProgramCreationDisabled}
                  onChangeChecked={updateIsProgramCreationDisabled}
                  testID="rewards-program-is-program-creation-disabled-switch"
                >
                  Retailer will not set up rewards program
                </Switch>
              )}
              {showGenerateTierOverrideButton && (
                <Button
                  loading={tierOverrideTriggered || isJobProcessing}
                  onClick={async () => {
                    let startTierOverride = false;
                    if (retailer.isErpIntegrationEnabled) {
                      startTierOverride = true;
                      await RetailerApi.processTierOverrides(retailer.id);
                    } else {
                      const hasTierOverrideOrders = (
                        await RetailerApi.hasTierOverrideOrders(retailer.id)
                      );
                      if (hasTierOverrideOrders) {
                        startTierOverride = true;
                        await RetailerApi.processTierOverrides(retailer.id);
                      } else {
                        setShowNoTierOverrideDataModal(true);
                      }
                    }
                    setTierOverrideTriggered(startTierOverride);
                    if (startTierOverride) {
                      setTimeout(() => {
                        void queryClient.invalidateQueries(QueryKeys.GET_JOB_IS_PROCESSING);
                        setTierOverrideTriggered(false);
                      }, 1000);
                    }
                  }}
                  testID="run-tier-overrides-button"
                  variant="outlined"
                >
                  Generate tier override
                </Button>
              )}
              {canAddProgram && (
                <Button
                  color="primary"
                  disabled={disabled}
                  onClick={() => setShowRewardsProgramModal(true)}
                  startIcon={<Add />}
                  testID="rewards-program-button"
                  variant="outlined"
                >
                  Add
                </Button>
              )}
            </>
          </Stack>
        </Stack>
        {!!agronomyPrograms?.length && (
          <>
            <VSpacer size="4" />
            <RewardsProgramsTable
              idsWithPointsEngineRun={programIdsWithPointsEngineRun}
              isInitializing={isInitializing}
              onDelete={(id) => {
                setSelectedProgramId(id);
                setShowDeleteConfirmationDialog(true);
              }}
              onInitializeFarmers={() => initializeFarmers()}
              onSelect={(id) => {
                setSelectedProgramId(id);
                setShowRewardsProgramModal(true);
              }}
              programs={agronomyPrograms}
            />
          </>
        )}
      </Stack>
      {showRewardsProgramModal && (
        <RewardsProgramModal
          existingProgram={selectedProgram}
          forcedStartDate={forcedStartDate}
          onClose={() => {
            setShowRewardsProgramModal(false);
            setSelectedProgramId(null);
          }}
          onCreateAgronomy={(body) => createProgram({ ...body, retailerId: retailer.id })}
          onUpdate={(updates) => {
            if (selectedProgramId) {
              updateProgram({ ...updates, id: selectedProgramId });
            }
            setSelectedProgramId(null);
          }}
          retailerId={retailer.id}
        />
      )}
      {showDeleteConfirmationDialog && !!selectedProgramId && (
        <ConfirmDialog
          cancelText="Cancel"
          confirmText="Delete"
          isLoading={isDeletingProgram}
          onCancel={() => {
            setShowDeleteConfirmationDialog(false);
            setSelectedProgramId(null);
          }}
          onConfirm={async () => {
            await deleteProgram(selectedProgramId);
            setSelectedProgramId(null);
            setShowDeleteConfirmationDialog(false);
          }}
          open
          showCloseButton={false}
          testID="delete-rewards-program-confirmation-dialog"
        >
          Are you sure you want to delete this rewards program?
        </ConfirmDialog>
      )}
      {showGenerateTierOverrideButton && showNoTierOverrideDataModal && (
        <ConfirmDialog
          cancelText="Close"
          onCancel={() => setShowNoTierOverrideDataModal(false)}
          open
          showCloseButton={false}
          testID="no-transactions-data-dialog"
          title="No transaction data"
        >
          Please upload transaction data for&nbsp;
          {formatMediumDate(
            getTierOverrideDates(getActiveOrFutureProgram()!).overrideStartDate, i18next,
          )} -&nbsp;
          {formatMediumDate(
            getTierOverrideDates(getActiveOrFutureProgram()!).overrideEndDate, i18next,
          )}
        </ConfirmDialog>
      )}
    </>
  );
};
